import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Dismiss24Regular, Warning24Regular } from "@fluentui/react-icons";

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: "450px",
    width: "100%",
  },
  dialogBody: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  content: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  },
  warningIcon: {
    color: tokens.colorPaletteYellowForeground1,
    fontSize: "24px",
    flexShrink: 0,
  },
  message: {
    color: tokens.colorNeutralForeground1,
    lineHeight: "1.5",
  },
});

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmAppearance?:
    | "primary"
    | "secondary"
    | "outline"
    | "subtle"
    | "transparent";
  showWarningIcon?: boolean;
}

export const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  confirmAppearance = "primary",
  showWarningIcon = true,
}: ConfirmDialogProps) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          <div className={styles.header}>
            <DialogTitle>
              {title || t("qtg.common.confirm", "Confirm")}
            </DialogTitle>
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={onClose}
            />
          </div>

          <DialogContent>
            <div className={styles.content}>
              {showWarningIcon && (
                <Warning24Regular className={styles.warningIcon} />
              )}
              <span className={styles.message}>{message}</span>
            </div>
          </DialogContent>

          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {cancelText || t("qtg.common.cancel", "Cancel")}
            </Button>
            <Button appearance={confirmAppearance} onClick={handleConfirm}>
              {confirmText || t("qtg.common.confirm", "Confirm")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ConfirmDialog;
